import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Chip,
  Portal,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import Loading from '../../../layouts/loading';
import { getAllLeaves } from '../../../services/leaveService';
import { getAllWfhRequests } from '../../../services/wfhService';
import { getAllHolidays } from '../../../services/companyHolidayService';
import { LEAVE_TYPE_LABELS, LEAVE_STATUS } from '../constants';

// Big enough for a month of requests for one company in a single call.
const MONTH_PAGE_SIZE = 1000;

const VISIBLE_STATUSES = [LEAVE_STATUS.APPROVED, LEAVE_STATUS.PENDING];

const KIND_STYLES = {
  leave: { letter: 'L', color: 'error', label: 'Leave' },
  wfh: { letter: 'W', color: 'info', label: 'WFH' },
};

const HALF_DAY_LABELS = { first_half: 'first half', second_half: 'second half' };

const fetchMonth = async (fetcher, monthStart) => {
  const response = await fetcher(
    { startDate: monthStart.format('YYYY-MM-DD'), endDate: monthStart.endOf('month').format('YYYY-MM-DD') },
    { page: 1, pageSize: MONTH_PAGE_SIZE }
  );
  return response?.data?.data || { rows: [], count: 0 };
};

const describeEntry = (entry) => {
  const kind = entry.kind === 'leave' ? LEAVE_TYPE_LABELS[entry.leaveType] || 'Leave' : 'WFH';
  const half = entry.isHalfDay ? ` · Half day (${HALF_DAY_LABELS[entry.halfDayType] || entry.halfDayType})` : '';
  const status = entry.status === LEAVE_STATUS.PENDING ? 'Pending approval' : 'Approved';
  return `${kind}${half} · ${status}`;
};

/**
 * Month grid of who is on leave / WFH, one row per employee.
 * Approved requests are filled, pending ones are outlined; click a cell to open the request.
 * Shows only what the list endpoints return for this user (managers: requests they review).
 *
 * With `focusUserId` (used from a request's details page) it opens on `initialMonth`, shows only that
 * employee until "Show whole team" is switched on, and rings the cells of the `highlight` request.
 */
const TeamCalendar = ({ showWfh = false, initialMonth, focusUserId, highlight }) => {
  const navigate = useNavigate();
  const [monthStart, setMonthStart] = useState(() => dayjs(initialMonth || undefined).startOf('month'));
  const [search, setSearch] = useState('');
  const [onlyFocused, setOnlyFocused] = useState(!!focusUserId);
  const monthKey = monthStart.format('YYYY-MM');

  const { data: leaveData, isLoading: isLoadingLeaves } = useQuery({
    queryKey: ['teamCalendarLeaves', monthKey],
    queryFn: () => fetchMonth(getAllLeaves, monthStart),
  });

  const { data: wfhData, isLoading: isLoadingWfh } = useQuery({
    queryKey: ['teamCalendarWfh', monthKey],
    queryFn: () => fetchMonth(getAllWfhRequests, monthStart),
    enabled: showWfh,
  });

  const {
    data: holidayData = [],
    isLoading: isLoadingHolidays,
    isError: isHolidayError,
  } = useQuery({
    queryKey: ['teamCalendarHolidays'],
    queryFn: async () => {
      const response = await getAllHolidays();
      return response?.data?.data || [];
    },
  });

  const holidaysByDate = useMemo(
    () =>
      new Map(
        holidayData.flatMap((yearData) =>
          Object.values(yearData || {}).flatMap((holidays) =>
            (Array.isArray(holidays) ? holidays : [])
              .filter((holiday) => holiday?.date && dayjs(holiday.date).isValid())
              .map((holiday) => [dayjs(holiday.date).format('YYYY-MM-DD'), holiday.name || 'Public holiday'])
          )
        )
      ),
    [holidayData]
  );

  const days = useMemo(
    () => Array.from({ length: monthStart.daysInMonth() }, (_, index) => monthStart.add(index, 'day')),
    [monthStart]
  );

  const employees = useMemo(() => {
    const monthEnd = monthStart.endOf('month');
    const byEmployee = new Map();

    const addRequests = (rows, kind) => {
      (rows || [])
        .filter((request) => VISIBLE_STATUSES.includes(request.status))
        .forEach((request) => {
          const employeeName = request.employee?.userName || request.employee?.email || 'Unknown';
          let day = dayjs(request.startDate).isBefore(monthStart) ? monthStart : dayjs(request.startDate);
          const lastDay = dayjs(request.endDate).isAfter(monthEnd) ? monthEnd : dayjs(request.endDate);
          while (!day.isAfter(lastDay, 'day')) {
            const key = day.format('YYYY-MM-DD');
            if (day.day() !== 0 && day.day() !== 6 && !holidaysByDate.has(key)) {
              if (!byEmployee.has(request.userId)) {
                byEmployee.set(request.userId, { id: request.userId, name: employeeName, days: {} });
              }
              const employee = byEmployee.get(request.userId);
              employee.days[key] = [...(employee.days[key] || []), { ...request, kind }];
            }
            day = day.add(1, 'day');
          }
        });
    };

    addRequests(leaveData?.rows, 'leave');
    if (showWfh) addRequests(wfhData?.rows, 'wfh');

    if (focusUserId && onlyFocused) {
      return [...byEmployee.values()].filter((employee) => employee.id === focusUserId);
    }

    const term = search.trim().toLowerCase();
    return [...byEmployee.values()]
      .filter((employee) => !term || employee.name.toLowerCase().includes(term))
      // The focused employee stays on top so the team rows read against them
      .sort((a, b) => (b.id === focusUserId) - (a.id === focusUserId) || a.name.localeCompare(b.name));
  }, [leaveData, wfhData, showWfh, monthStart, search, focusUserId, onlyFocused, holidaysByDate]);

  // Approved leaves per day, for the "On leave" footer row.
  const onLeaveCount = (dayKey) =>
    employees.filter((employee) =>
      (employee.days[dayKey] || []).some((entry) => entry.kind === 'leave' && entry.status === LEAVE_STATUS.APPROVED)
    ).length;

  const isTruncated =
    (leaveData?.count || 0) > (leaveData?.rows?.length || 0) ||
    (showWfh && (wfhData?.count || 0) > (wfhData?.rows?.length || 0));

  const isWeekend = (day) => day.day() === 0 || day.day() === 6;
  const isToday = (day) => day.isSame(dayjs(), 'day');

  const renderEntry = (entry, { clickable = true } = {}) => {
    const style = KIND_STYLES[entry.kind];
    const isApproved = entry.status === LEAVE_STATUS.APPROVED;
    const isHighlighted = highlight && highlight.kind === entry.kind && highlight.id === entry.id;
    return (
      <Tooltip key={`${entry.kind}-${entry.id}`} title={describeEntry(entry)} arrow>
        <Box
          onClick={
            clickable ? () => navigate(entry.kind === 'leave' ? `/leaves/${entry.id}` : `/wfh/${entry.id}`) : undefined
          }
          sx={{
            cursor: clickable ? 'pointer' : 'default',
            minWidth: 24,
            borderRadius: 0.75,
            px: 0.25,
            typography: 'caption',
            fontWeight: 700,
            lineHeight: '20px',
            textAlign: 'center',
            border: 1,
            borderStyle: isApproved ? 'solid' : 'dashed',
            borderColor: `${style.color}.main`,
            bgcolor: isApproved ? `${style.color}.main` : 'transparent',
            color: isApproved ? `${style.color}.contrastText` : `${style.color}.main`,
            ...(isHighlighted && {
              outline: (theme) => `2px solid ${theme.palette.text.primary}`,
              outlineOffset: 1,
            }),
          }}
        >
          {entry.isHalfDay ? `½${style.letter}` : style.letter}
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      {/* Portalled to <body>: the calendar sits inside a Card (theme gives Cards zIndex 0), which would
          otherwise trap the backdrop below the sidebar. modal + 1 also keeps it above the plan dialog. */}
      <Portal>
        <Box sx={{ position: 'relative', zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <Loading open={isLoadingLeaves || (showWfh && isLoadingWfh) || isLoadingHolidays} />
        </Box>
      </Portal>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => setMonthStart((prev) => prev.subtract(1, 'month'))} aria-label="Previous month">
            <Iconify icon="eva:arrow-ios-back-fill" />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center' }}>
            {monthStart.format('MMMM YYYY')}
          </Typography>
          <IconButton onClick={() => setMonthStart((prev) => prev.add(1, 'month'))} aria-label="Next month">
            <Iconify icon="eva:arrow-ios-forward-fill" />
          </IconButton>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', ml: 1 }}
            onClick={() => setMonthStart(dayjs().startOf('month'))}
          >
            Today
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          {focusUserId && (
            <FormControlLabel
              control={<Switch checked={!onlyFocused} onChange={(e) => setOnlyFocused(!e.target.checked)} />}
              label="Show whole team"
            />
          )}
          {!onlyFocused && (
            <TextField
              size="small"
              placeholder="Search employee"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
        {Object.entries(KIND_STYLES)
          .filter(([kind]) => showWfh || kind === 'leave')
          .map(([kind, style]) => (
            <Stack key={kind} direction="row" spacing={1} alignItems="center">
              {renderEntry({ kind, id: `legend-${kind}-a`, status: LEAVE_STATUS.APPROVED }, { clickable: false })}
              <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                {style.label} approved
              </Typography>
              {renderEntry({ kind, id: `legend-${kind}-p`, status: LEAVE_STATUS.PENDING }, { clickable: false })}
              <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                {style.label} pending
              </Typography>
            </Stack>
          ))}
        <Chip
          label="Public holiday"
          size="small"
          sx={{ bgcolor: 'info.lighter', alignSelf: 'center' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          ½ = half day{highlight ? ' · Ringed = this request' : ''} · Click a cell to open the request
        </Typography>
      </Stack>

      {isTruncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This month has more requests than can be shown at once; some may be missing.
        </Alert>
      )}

      {isHolidayError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Public holidays could not be loaded, so leave and WFH entries may appear on holiday dates.
        </Alert>
      )}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { px: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', minWidth: 160 }}>
                Employee
              </TableCell>
              {days.map((day) => {
                const holidayName = holidaysByDate.get(day.format('YYYY-MM-DD'));
                return (
                  <TableCell
                    key={day.format('D')}
                    align="center"
                    title={holidayName}
                    sx={{
                      minWidth: 32,
                      bgcolor: holidayName ? 'info.lighter' : isWeekend(day) ? 'action.hover' : undefined,
                      color: isToday(day) ? 'primary.main' : undefined,
                    }}
                  >
                    <Typography variant="caption" display="block" color="text.secondary">
                      {day.format('dd').charAt(0)}
                    </Typography>
                    <Typography variant="caption" fontWeight={isToday(day) ? 700 : 400}>
                      {day.format('D')}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length > 0 ? (
              <>
                {employees.map((employee) => (
                  <TableRow key={employee.id} hover selected={!onlyFocused && employee.id === focusUserId}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        whiteSpace: 'nowrap',
                        fontWeight: employee.id === focusUserId ? 700 : undefined,
                      }}
                    >
                      {employee.name}
                    </TableCell>
                    {days.map((day) => {
                      const dayKey = day.format('YYYY-MM-DD');
                      const holidayName = holidaysByDate.get(dayKey);
                      return (
                        <TableCell
                          key={dayKey}
                          align="center"
                          title={holidayName}
                          sx={{
                            bgcolor: holidayName ? 'info.lighter' : isWeekend(day) ? 'action.hover' : undefined,
                          }}
                        >
                          <Stack spacing={0.25}>{(employee.days[dayKey] || []).map((entry) => renderEntry(entry))}</Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {!onlyFocused && (
                <TableRow>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2">On leave</Typography>
                  </TableCell>
                  {days.map((day) => {
                    const dayKey = day.format('YYYY-MM-DD');
                    const holidayName = holidaysByDate.get(dayKey);
                    const count = onLeaveCount(dayKey);
                    return (
                      <TableCell
                        key={day.format('D')}
                        align="center"
                        title={holidayName}
                        sx={{
                          bgcolor: holidayName ? 'info.lighter' : isWeekend(day) ? 'action.hover' : undefined,
                        }}
                      >
                        <Typography variant="subtitle2" color={count ? 'error.main' : 'text.disabled'}>
                          {count || '-'}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={days.length + 1} align="center">
                  {`${showWfh ? 'No leave or WFH' : 'No leave'}${onlyFocused ? ' for this employee' : ''} this month`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

TeamCalendar.propTypes = {
  showWfh: PropTypes.bool,
  initialMonth: PropTypes.string,
  focusUserId: PropTypes.number,
  highlight: PropTypes.shape({ kind: PropTypes.oneOf(['leave', 'wfh']), id: PropTypes.number }),
};

export default TeamCalendar;
